import React, { useState, useEffect } from 'react';

const JobSeekerEditForm = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    type: '',
    experience: '',
    education: '',
    certifications: [],
    criminalRecordCheck: false,
    languages: [],
    hourlyRate: '',
    availability: {
      monday: { start: '', end: '' },
      tuesday: { start: '', end: '' },
      wednesday: { start: '', end: '' },
      thursday: { start: '', end: '' },
      friday: { start: '', end: '' },
      saturday: { start: '', end: '' },
      sunday: { start: '', end: '' }
    },
    phone: '',
    address: '',
    email: '',
    displayName: ''
  });

  useEffect(() => {
    if (profile) setFormData(profile);
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...prev[name], value]
        : prev[name].filter(item => item !== value)
    }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="edit-profile-form">
      <div className="form-group">
        <label htmlFor="displayName">Display Name</label>
        <input
          type="text"
          id="displayName"
          name="displayName"
          value={formData.displayName || ''}
          onChange={handleInputChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="phone">Phone Number</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email || ''}
          onChange={handleInputChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="address">Address</label>
        <textarea
          id="address"
          name="address"
          value={formData.address || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="type">Job Type</label>
        <select
          id="type"
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Type</option>
          <option value="nanny">Nanny</option>
          <option value="educator">Early Childhood Educator</option>
          <option value="both">Both</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="experience">Work Experience</label>
        <textarea
          id="experience"
          name="experience"
          value={formData.experience}
          onChange={handleInputChange}
          rows="4"
        />
      </div>
      <div className="form-group">
        <label htmlFor="education">Education</label>
        <textarea
          id="education"
          name="education"
          value={formData.education}
          onChange={handleInputChange}
          rows="4"
        />
      </div>
      <div className="form-group">
        <label>Certifications</label>
        <div className="checkbox-grid">
          {["First Aid", "CPR", "Montessori", "Early Childhood Education"].map(cert => (
            <div key={cert} className="checkbox-item">
              <input
                type="checkbox"
                id={`cert-${cert}`}
                name="certifications"
                value={cert}
                checked={formData.certifications.includes(cert)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`cert-${cert}`}>{cert}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="form-group">
        <div className="checkbox-item">
          <input
            type="checkbox"
            id="criminalRecordCheck"
            name="criminalRecordCheck"
            checked={formData.criminalRecordCheck}
            onChange={handleInputChange}
          />
          <label htmlFor="criminalRecordCheck">Criminal Record Check Completed</label>
        </div>
      </div>
      <div className="form-group">
        <label>Languages</label>
        <div className="checkbox-grid">
          {["English", "Spanish", "French", "Mandarin", "Hindi", "Punjabi"].map(lang => (
            <div key={lang} className="checkbox-item">
              <input
                type="checkbox"
                id={`lang-${lang}`}
                name="languages"
                value={lang}
                checked={formData.languages.includes(lang)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`lang-${lang}`}>{lang}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="hourlyRate">Hourly Rate ($)</label>
        <input
          type="number"
          id="hourlyRate"
          name="hourlyRate"
          value={formData.hourlyRate}
          onChange={handleInputChange}
          min="0"
          step="0.01"
        />
      </div>
      <div className="form-group">
        <label>Availability</label>
        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
          <div key={day} className="day-availability">
            <h3>{day.charAt(0).toUpperCase() + day.slice(1)}</h3>
            <div className="availability-time-row">
              <label htmlFor={`${day}-start`} style={{ marginRight: 8 }}>Start:</label>
              <input
                type="time"
                id={`${day}-start`}
                value={formData.availability[day].start}
                onChange={e => handleAvailabilityChange(day, 'start', e.target.value)}
                style={{ marginRight: 16 }}
              />
              <label htmlFor={`${day}-end`} style={{ marginRight: 8 }}>End:</label>
              <input
                type="time"
                id={`${day}-end`}
                value={formData.availability[day].end}
                onChange={e => handleAvailabilityChange(day, 'end', e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="form-actions">
        <button type="submit" className="save-button">Save</button>
        <button type="button" className="cancel-button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

export default JobSeekerEditForm; 